import React, { useCallback, useMemo, useState } from 'react';
import type {
  CoreSnippetFieldsFragment,
  DeleteSnippetMutationFn,
  DeleteSnippetMutationResult,
  GetSnippetsLazyQueryHookResult
} from 'models/graphql';

import { triggerErrorToast } from 'helpers/triggerErrorToast';
import { SnippetsViewLayout, SnippetsViewLayoutProps } from './Snippets.view';
import { useDidMount } from 'hooks/useDidMount';
import { SnippetCardViewProps } from './SnippetCard/SnippetCard.view';

export type SnippetsControllerProps = {
  deleteSnippetMutation: DeleteSnippetMutationFn;
  deleteSnippetMutationData: DeleteSnippetMutationResult;
  getSnippetsQuery: GetSnippetsLazyQueryHookResult[0];
  snippets: CoreSnippetFieldsFragment[] | undefined;
  snippetsLoading: boolean;
  workspaceId: string;
  context: SnippetsViewLayoutProps['context'];
  onAddToPostClick: SnippetCardViewProps['onAddToPostClick'] | undefined;
};

export const SnippetsController = (props: SnippetsControllerProps): JSX.Element => {
  const [isCreateSnippetDialogOpen, setIsCreateSnippetDialogOpen] = useState(false);
  const [snippetToEdit, setSnippetToEdit] = useState<CoreSnippetFieldsFragment | null>(null);
  const [idSnippetToDelete, setIdSnippetToDelete] = useState<string | null>(null);

  const onCreateSnippetDialogClose = () => setIsCreateSnippetDialogOpen(false);
  const onDeleteSnippetDialogClose = () => setIdSnippetToDelete(null);
  const onEditSnippetDialogClose = () => setSnippetToEdit(null);
  const onCreateSnippetDialogOpen = () => setIsCreateSnippetDialogOpen(true);

  const {
    getSnippetsQuery,
    deleteSnippetMutation,
    snippets,
    snippetsLoading,
    context,
    onAddToPostClick,
    deleteSnippetMutationData
  } = props;

  useDidMount(() => {
    getSnippetsQuery();
  });
  const onDeleteClick = (snippetId: string) => {
    setIdSnippetToDelete(snippetId);
  };
  const deleteSnippet = async () => {
    if (!idSnippetToDelete) return;

    await deleteSnippetMutation({
      variables: { snippetId: idSnippetToDelete }
    }).catch(triggerErrorToast('Delete Snippet Error'));
    setIdSnippetToDelete(null);
  };

  const setSnippetToEditHandler = useCallback(
    (snippedId: string) => setSnippetToEdit(snippets?.find(({ id }) => id === snippedId) || null),
    [snippets]
  );

  const sortedSnippetsByName = useMemo(() => {
    if (!snippets?.length) return [];

    return [...snippets].sort((a, b) => (a.name || '').localeCompare(b.name as string));
  }, [snippets]);

  const viewProps: SnippetsViewLayoutProps = {
    idSnippetToDelete,
    snippetDeleteLoading: deleteSnippetMutationData.loading,
    onDeleteClick,
    onDeleteSnippetDialogClose,
    onCreateSnippetDialogOpen,
    context,
    deleteSnippet,
    setSnippetToEdit: setSnippetToEditHandler,
    snippets: sortedSnippetsByName,
    snippetsLoading,
    snippetToEdit,
    isCreateSnippetDialogOpen,
    onCreateSnippetDialogClose,
    onEditSnippetDialogClose,
    onAddToPostClick
  };

  return <SnippetsViewLayout {...viewProps} />;
};
